import { PageSection, PageWrapper } from '@src/components';
import { useLazyGetPackagesQuery } from '@src/services/packages/packageApiSlice';
import { useLazyGetCountryQuery } from '@src/services/country/countryApiSlice';
import { useEffect, useMemo, useState } from 'react';
import { PackageList } from './components/PackageList';
import { ExpireInFilterBar } from './components/ExpireInFilterBar';
import { PackageDetails } from './PackageDetails';
import { useLazyGetRegionQuery } from '@src/services/region/regionApiSlice';
import { PackageHeaderSecondVersion } from './components';
import { Package } from '@src/services/packages/packageApiSliceTypes';
import { Country } from '@src/services/country/countryApiSliceTypes';
import { Region } from '@src/services/region/regionApiSliceTypes';
import { ListItemSkeleton } from '@src/components/Loading/ListItemSkeleton';
import { useQueryParams } from '@src/hooks';

export function Packages() {
  const { getQueryParam, setQueryParam, deleteQueryParam } = useQueryParams();
  const [selectedPackage, setSelectedPackage] = useState<Package>();
  const [selectedExpireIn, setSelectedExpireIn] = useState<number | null>(null);
  const [countryInfo, setCountryInfo] = useState<Country>();
  const [regionInfo, setRegionInfo] = useState<Region>();
  const [openPackageDetails, setOpenPackageDetails] = useState(false);

  const countryId = getQueryParam('countryId') ?? undefined;
  const regionId = getQueryParam('regionId') ?? undefined;
  const packageId = getQueryParam('packageId') ?? undefined;

  const [fetchPackages, { data: packages, isLoading: packagesLoading }] =
    useLazyGetPackagesQuery();
  const [getCountry, { isLoading: countryLoading }] = useLazyGetCountryQuery();
  const [getRegion, { data: region, isLoading: regionLoading }] =
    useLazyGetRegionQuery();

  useEffect(() => {
    if (countryId) {
      fetchPackages({ countryId, sortBy: 'price', sortOrder: 'ASC' });
      getCountry({ id: countryId }).unwrap().then(setCountryInfo);
    } else if (regionId) {
      fetchPackages({ regionId, sortBy: 'price', sortOrder: 'ASC' });
      getRegion({ id: regionId, includeCountries: true })
        .unwrap()
        .then(setRegionInfo);
    }
  }, [countryId, regionId, fetchPackages, getCountry, getRegion]);

  useEffect(() => {
    if (!packages?.items || !packageId) return;

    const foundPackage = packages.items.find((p) => p.id === packageId);

    if (foundPackage) {
      setSelectedPackage(foundPackage);
      setOpenPackageDetails(true);
    }
  }, [packages, packageId]);

  const expireInOptions = useMemo(() => {
    if (!packages?.items) return [];
    const values = [...new Set(packages.items.map((p) => Number(p.expireIn)))];
    return values.sort((a, b) => a - b);
  }, [packages]);

  const filteredPackages = useMemo(() => {
    if (!selectedExpireIn) return packages?.items || [];
    return packages?.items.filter((p) => p.expireIn === selectedExpireIn) || [];
  }, [selectedExpireIn, packages]);

  const isLoading = packagesLoading || countryLoading || regionLoading;

  if (isLoading || (!countryId && !regionId)) {
    return <ListItemSkeleton />;
  }

  const handleSelectItem = (item: Package) => {
    setOpenPackageDetails(true);
    setSelectedPackage(item);

    setQueryParam('packageId', item.id);
  };

  const handleClose = () => {
    setOpenPackageDetails(false);
    setTimeout(() => {
      setSelectedPackage(undefined);

      deleteQueryParam('packageId');
    }, 300);
  };

  return (
    <PageWrapper rootClassName='space-y-2'>
      {countryId && countryInfo && (
        <PackageHeaderSecondVersion
          type='country'
          name={countryInfo?.name}
          image={countryInfo?.image}
          flag={countryInfo?.flag}
        />
      )}
      {regionId && regionInfo && (
        <PackageHeaderSecondVersion
          type='region'
          name={regionInfo?.name}
          image={regionInfo?.image}
          flag={regionInfo.imageIcon ?? regionInfo?.image}
        />
      )}
      {regionId && (
        <PageSection className='bg-wl-background !my-4 space-y-4 py-6 !px-4 text-wl-on-background'>
          <div className='text-M-16'>Countries covered</div>
          <div className='text-R-16 flex items-center'>
            {region?.countries?.map((c) => c.name).join(' · ')}
          </div>
        </PageSection>
      )}
      <PageSection className='w-full md:w-fit bg-wl-background !my-4 py-4 px-6'>
        <ExpireInFilterBar
          expireInOptions={expireInOptions}
          selectedExpireIn={selectedExpireIn}
          onChange={setSelectedExpireIn}
        />
      </PageSection>

      <PackageList packages={filteredPackages} onItemClick={handleSelectItem} />
      {selectedPackage && (
        <PackageDetails
          isOpen={openPackageDetails}
          onClose={handleClose}
          item={selectedPackage}
          flagSrc={
            countryId
              ? countryInfo?.flag
              : (regionInfo?.imageIcon ?? regionInfo?.image)
          }
        />
      )}
    </PageWrapper>
  );
}
